"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyDiscounts = applyDiscounts;
const errors_1 = require("../errors");
const cents_1 = require("../money/cents");
const allocate_1 = require("../money/allocate");
// Résolution des cibles d'une remise (ruling Task 2, spec §3.2) :
// itemReferenceIds d'abord (référence inconnue → erreur), sinon itemIndices
// en repli legacy (indice hors bornes → erreur), sinon toutes les lignes.
function targetsOf(discount, items) {
    const { itemReferenceIds, itemIndices } = discount;
    if (itemReferenceIds && itemReferenceIds.length > 0) {
        return itemReferenceIds.map((referenceId) => {
            const index = items.findIndex((item) => item.referenceId === referenceId);
            if (index === -1) {
                throw new errors_1.UnknownReferenceError(`applyDiscounts : référence inconnue « ${referenceId} »`);
            }
            return index;
        });
    }
    if (itemIndices && itemIndices.length > 0) {
        return itemIndices.map((index) => {
            if (!Number.isInteger(index) || index < 0 || index >= items.length) {
                throw new errors_1.UnknownReferenceError(`applyDiscounts : indice hors bornes « ${index} »`);
            }
            return index;
        });
    }
    return items.map((_, index) => index);
}
// Répartition des montants post-remise sur les composants de la ligne.
//
// Elle se fait PAR TAXE : chaque part de `vatSharesAfterDiscount` est répartie
// au plus fort reste (`allocateProportionally`, la même passe que celle qui a
// produit ces parts) entre les composants qui portent cette taxe, au prorata de
// leur montant d'AVANT remise. Répartir d'un seul coup sur tous les composants
// serait plus direct mais ne garantirait pas l'égalité par taxe : la méthode du
// plus fort reste n'est pas associative, et deux passes indépendantes peuvent
// s'écarter d'un centime. Ici les deux invariants tiennent par construction.
function componentsAfterDiscount(item, vatSharesAfterDiscount) {
    // Le composant « base » existe toujours, même à 0 € (ligne offerte) : le
    // ticket doit pouvoir nommer la ligne et son taux.
    const weights = [
        // Une base PAR PART ventilée (spec 2026-10-01 §4) : un menu mixte produit
        // plusieurs composants « base », un par taux, dans l'ordre de `baseParts`.
        ...item.baseParts.map((part) => ({
            kind: 'base',
            referenceId: item.referenceId,
            tax: part.tax,
            amount: part.amount,
        })),
        ...item.extras.map((extra) => ({
            kind: 'extra',
            referenceId: extra.referenceId,
            tax: extra.tax,
            amount: extra.amount,
        })),
    ];
    const shareByTax = new Map(vatSharesAfterDiscount.map((share) => [share.tax.id, share.amount]));
    const amounts = weights.map(() => 0);
    for (const [taxId, shareAmount] of shareByTax) {
        const positions = weights.flatMap((component, index) => (component.tax.id === taxId ? [index] : []));
        const componentWeights = positions.map((index) => weights[index]?.amount ?? 0);
        const total = componentWeights.reduce((sum, weight) => sum + weight, 0);
        // Une part de TVA ne peut porter sur aucun composant à poids nul :
        // `addShare` (compute-item) n'enregistre jamais une part à montant nul,
        // donc `total > 0` dès que `shareAmount` existe.
        if (positions.length === 0 || total === 0)
            continue;
        const parts = (0, allocate_1.allocateProportionally)(shareAmount, componentWeights);
        positions.forEach((index, position) => {
            amounts[index] = parts[position] ?? 0;
        });
    }
    return weights.map((component, index) => ({ ...component, amount: amounts[index] ?? 0 }));
}
function applyDiscounts(items, discounts, promotions) {
    // Totaux restants par ligne, entamés d'abord par les promotions puis,
    // séquentiellement, par chaque remise utilisateur.
    const remaining = items.map((item) => item.subtotal);
    const promotionShares = items.map((item, index) => Math.min(promotions?.perItem[index] ?? 0, item.subtotal));
    items.forEach((_, index) => {
        remaining[index] = (remaining[index] ?? 0) - (promotionShares[index] ?? 0);
    });
    // Les remises de commande d'une promotion se répartissent au plus fort reste
    // sur ce qu'il reste des lignes, exactement comme une remise globale.
    const remainingTotal = remaining.reduce((sum, amount) => sum + amount, 0);
    const orderPromotion = Math.min(Math.max(promotions?.order ?? 0, 0), remainingTotal);
    if (orderPromotion > 0) {
        const parts = (0, allocate_1.allocateProportionally)(orderPromotion, remaining);
        items.forEach((_, index) => {
            const part = parts[index] ?? 0;
            remaining[index] = (remaining[index] ?? 0) - part;
            promotionShares[index] = (promotionShares[index] ?? 0) + part;
        });
    }
    const computedDiscounts = [];
    for (const discount of discounts) {
        // Spec §7 : jamais d'échec silencieux sur l'argent — une remise invalide
        // doit faire échouer le calcul, pas produire un montant erroné (F3).
        if (discount.type === 'PERCENTAGE') {
            if (!Number.isFinite(discount.percent) || discount.percent < 0 || discount.percent > 100) {
                throw new errors_1.InvalidPercentError(`applyDiscounts : percent de remise invalide, reçu : ${discount.percent}`);
            }
        }
        else {
            (0, cents_1.assertCents)(discount.amount, 'applyDiscounts.discount.amount');
            if (discount.amount < 0) {
                throw new errors_1.InvalidAmountError(`applyDiscounts : amount de remise négatif, reçu : ${discount.amount}`);
            }
        }
        const targetIndices = targetsOf(discount, items);
        const targetTotal = targetIndices.reduce((sum, index) => sum + (remaining[index] ?? 0), 0);
        const requested = discount.type === 'PERCENTAGE' ? (0, cents_1.applyPercent)(targetTotal, discount.percent) : discount.amount;
        // Plafonnée au total ciblé (ruling R3) : jamais de ligne négative.
        const amount = Math.min(requested, targetTotal);
        computedDiscounts.push({ discount, amount });
        if (amount <= 0)
            continue;
        const weights = targetIndices.map((index) => remaining[index] ?? 0);
        const parts = (0, allocate_1.allocateProportionally)(amount, weights);
        targetIndices.forEach((itemIndex, position) => {
            remaining[itemIndex] = (remaining[itemIndex] ?? 0) - (parts[position] ?? 0);
        });
    }
    const resultItems = items.map((item, index) => {
        const total = remaining[index] ?? 0;
        const promotionDiscount = promotionShares[index] ?? 0;
        const discountTotal = item.subtotal - promotionDiscount - total;
        // Un poids nul dans vatShares est impossible : addShare (compute-item)
        // n'insère jamais de part à montant nul. total === 0 couvre le seul cas
        // où allocateProportionally ne serait pas appelable (remise ayant tout
        // consommé).
        const weights = item.vatShares.map((share) => share.amount);
        const amounts = total === 0 ? weights.map(() => 0) : (0, allocate_1.allocateProportionally)(total, weights);
        const vatSharesAfterDiscount = item.vatShares.map((share, shareIndex) => ({
            tax: share.tax,
            amount: amounts[shareIndex] ?? 0,
        }));
        return {
            ...item,
            promotionDiscount,
            discountTotal,
            total,
            vatSharesAfterDiscount,
            components: componentsAfterDiscount(item, vatSharesAfterDiscount),
        };
    });
    return { items: resultItems, discounts: computedDiscounts };
}
